/* eslint-disable jsx-a11y/label-has-associated-control */
import React, { useState, useRef, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { Context as CartContext } from '../../../context/cart';
import { Context as CheckoutContext } from '../../../context/checkout';
import { Context as RestaurantContext } from '../../../context/restaurant';
import ConfirmButton from '../../../components/buttons/confirmButton';
import CartItem from './cartItem';
import CheckoutTotal from '../../checkout/components/total';
import { calculateCartSum } from '../../../services/cartService';
import { useAutosizeTextArea } from '../../../hooks/useAutosizeTextArea';
import GuestCountSelect from './guestCountSelect';
import CartRecommendations from './cartRecommendations';
import BottomModal from '../../../components/modal/bottomModal';
import ProductModal from '../../menu/components/order/productModal';
import { useBackPress } from '../../../hooks/useBackPress';
import { useLocalStorage } from '../../../hooks/useLocalStorage';
import WaiterNote from './waiterNote';
import RoomNumberSelect from './roomNumberSelect';

const CartProducts = ({ cart, onRemove }) => {
  const navigate = useNavigate();
  const { t } = useTranslation();

  const noteRef = useRef(null);
  const [note, setNote] = useState('');
  useAutosizeTextArea(noteRef.current, note);

  const [totalGuests, setTotalGuests] = useState(0);
  const [roomNumber, setRoomNumber] = useState('');

  const [selectedProduct, setSelectedProduct] = useState(null);
  const [productDialogOpen, setProductDialogOpen] = useState(false);

  const { state: cartState, addProductToCart } = useContext(CartContext);
  const { setOrderBill } = useContext(CheckoutContext);
  const { state: restaurantState } = useContext(RestaurantContext);

  const total = calculateCartSum({ cart });
  const totalAfterDiscount = total - (cartState.totalDiscounted || 0);

  const [ageConfirmed, setAgeConfirmed] = useLocalStorage(
    `age-confirmed-${restaurantState.restaurant.id}`,
    false,
  );

  const { handleBackOnModalOpen, handleBackOnModalClose } = useBackPress('/cart', () =>
    setProductDialogOpen(false),
  );

  const canAskGuestCount = !restaurantState.restaurant.dontRequireGuests;
  const requireRoomNumber = restaurantState.restaurant.requireRoomNumber || false;
  const canLeaveComment = !restaurantState.restaurant.disableComment;

  const isContinueDisabled = () => {
    if (!cart || cart.length === 0) {
      return true;
    }

    if (requireRoomNumber && !roomNumber) {
      return true;
    }

    if (!canAskGuestCount) {
      return false;
    }

    if (canAskGuestCount) {
      return !totalGuests;
    }

    return false;
  };

  return (
    <div className="mt-4  flex flex-col justify-between flex-1">
      <div className="mb-9">
        <p className="font-primaryMedium text-2xl text-primary mb-5">{t('header.cart')}</p>
        {cart &&
          cart.map((item) => (
            <CartItem key={`${item.productId}-${item.index}`} product={item} onRemove={onRemove} />
          ))}
        <div className="pt-2">
          <CheckoutTotal
            total={totalAfterDiscount}
            discount={cartState.totalDiscounted}
            noDivider
          />
        </div>
      </div>
      <div className="mt-auto">
        {requireRoomNumber && (
          <div className="border-b border-border pb-1">
            <div className="mt-5">
              <RoomNumberSelect roomNumber={roomNumber} onChange={setRoomNumber} />
            </div>
          </div>
        )}
        {canAskGuestCount && (
          <div className="border-b border-border pb-5">
            <div className="mt-5">
              <GuestCountSelect totalGuests={totalGuests} onChange={setTotalGuests} />
            </div>
          </div>
        )}
        {canLeaveComment && (
          <div className="mt-5">
            <WaiterNote note={note} onChange={setNote} />
          </div>
        )}
        <CartRecommendations
          productCategories={restaurantState.productCategories}
          onSelect={(product) => {
            setSelectedProduct(product);
            setProductDialogOpen(true);
            handleBackOnModalOpen();
          }}
        />
      </div>
      <ConfirmButton
        onClick={() => {
          setOrderBill({
            subtotal: total,
            discount: cartState.totalDiscounted,
            cart,
            note,
            totalGuests,
            roomNumber,
          });
          navigate('/checkout');
        }}
        customText={t('buttons.continueToPayment')}
        sticky
        disabled={isContinueDisabled()}
      />
      <BottomModal
        noHeader
        noPadding
        content={
          <ProductModal
            product={selectedProduct}
            onAdd={(data) => {
              addProductToCart(data, restaurantState.discountCartService);
              setProductDialogOpen(false);
              handleBackOnModalClose();
            }}
            refresher={productDialogOpen}
            onClose={() => {
              setProductDialogOpen(false);
              handleBackOnModalClose();
            }}
            discountMenuService={restaurantState.discountMenuService}
            needConfirmAge={!ageConfirmed}
            restrictionsAge={restaurantState.restaurant.restrictionsAge}
            onAgeConfirmed={() => setAgeConfirmed(true)}
          />
        }
        onClose={() => {
          setProductDialogOpen(false);
          handleBackOnModalClose();
        }}
        onOutClick={() => {
          setProductDialogOpen(false);
          handleBackOnModalClose();
        }}
        open={productDialogOpen}
      />
    </div>
  );
};

export default CartProducts;
